import {ManyResponse} from '@backend/responses/many.response';
import {SCondition, RequestQueryBuilder} from '@nestjsx/crud-request';
import {Injectable} from '@angular/core';
import {Burly} from 'kb-burly';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {UnitsResponse} from '../responses';
import {Observable} from 'rxjs';
import {BaseUnit, BaseUnitHistory} from '../models';
import {map} from 'rxjs/operators';
import {PartNumber} from '../models';

@Injectable({
  providedIn: 'root'
})
export class TorchUnitsService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  public queryUnits(pageSize: number = 50,
                    currentPage: number = 0,
                    sortDirection: 'ASC' | 'DESC' | '' = 'ASC',
                    sortBy: string = 'shelfLifeDaysRemaining',
                    filters: SCondition | null): Observable<ManyResponse<BaseUnit>> {
    const qb = RequestQueryBuilder.create();

    qb.setPage(currentPage + 1).setLimit(pageSize);

    if (!!sortDirection) {
      qb.sortBy({field: sortBy, order: sortDirection});
    }

    if (!!filters) {
      qb.search(filters);
    }

    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/units')
      .appendRawQueryString(qb.query(false))
      .get;

    return this.httpClient.get<ManyResponse<BaseUnit>>(endpoint);
  }

  serialNumberSearch(partNumber: PartNumber, limit: number = 25): Observable<BaseUnit[]> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addQuery('limit', limit)
      .addQuery('filter', `${partNumber.type}||$eq||"${partNumber.partNumber}"`)
      .get;

    return this.httpClient.get<UnitsResponse>(endpointURL).pipe(
      map(response => response.data)
    );
  }

  partNumberSearch(partNumber: string): Observable<PartNumber[]> {
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addSegment('/search')
      .addQuery('partNumber', partNumber)
      .get;

    return this.httpClient.get<PartNumber[]>(endpointURL);
  }

  singleUnitSearch(companyPartNumber: string, companySerialNumber: string, limit: number = 1): Observable<BaseUnit[]> {
    let endpointURL = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addQuery('limit', limit)
      .get;

    const filter1 = 'companyPartNumber||$eq||"' + companyPartNumber + '"';
    const filter2 = 'companySerialNumber||$eq||"' + companySerialNumber + '"';

    endpointURL = `${endpointURL}&filter=${encodeURIComponent(filter1)}`;
    endpointURL = `${endpointURL}&filter=${encodeURIComponent(filter2)}`;

    return this.httpClient.get<UnitsResponse>(endpointURL).pipe(
      map(response => response.data)
    );
  }

  singleMsnSearch(filter?: string | string[], limit: number = 1): Observable<BaseUnit[]> {
    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addQuery('limit', limit);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach(filterValue => {
            finalURL = `${finalURL}&filter=${encodeURIComponent(filterValue)}`;
          });
        }
      } else {
        finalURL = `${finalURL}&or=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<UnitsResponse>(finalURL).pipe(
      map(response => response.data)
    );
  }

  public getDistinctPartNumbers(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addSegment('/partNumbers')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ CPN: string }]>(endpoint).pipe(
      map(response => response.map(v => v.CPN))
    );
  }

  public getDistinctSerialNumbersByPartNumber(partNumber: string, contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addSegment('/serialNumbers/' + partNumber)
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ MSN: string }]>(endpoint).pipe(
      map(response => response.map(v => v.MSN))
    );
  }

  public getDistinctShelfLifeStations(contains): Observable<string[]> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addSegment('/stationsShelfLife')
      .addQuery('contains', contains)
      .get;

    return this.httpClient.get<[{ LOCN: string }]>(endpoint).pipe(
      map(response => response.map(v => v.LOCN))
    );
  }

  public getSingleUnitRecord(partNumber: string, partSerial: string): Observable<BaseUnit> {
    let finalURL;
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/units')
      .addSegment('/find')
      .addSegment('/cpn')
      .addSegment('/' + partNumber)
      .addSegment('/msn')
      .addSegment('/' + partSerial);

    finalURL = endpoint.get;

    return this.httpClient.get<BaseUnit[]>(finalURL).pipe(
      map(response => response[0])
    );
  }
}
